
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { Copy, Zap, CheckCircle, Clock, XCircle, ArrowLeft } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { Invoice } from "../../../types";
import { Card } from "../../ui/Card";
import { Button } from "../../ui/Button";

export const InvoicePreview = ({ invoice, link, onReset }: { invoice: Invoice; link: string; onReset: () => void; }) => {
  const copy = (text: string) => {
    navigator.clipboard.writeText(text);
    // Add toast notification later
  };
  
  const getStatusChip = () => {
      switch(invoice.status) {
          case 'paid': return <div className="flex items-center gap-1.5 px-2 py-1 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs font-bold rounded-full"><CheckCircle size={14}/> Paid</div>;
          case 'unpaid': return <div className="flex items-center gap-1.5 px-2 py-1 bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 text-xs font-bold rounded-full"><Clock size={14}/> Unpaid</div>;
          case 'expired': return <div className="flex items-center gap-1.5 px-2 py-1 bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 text-xs font-bold rounded-full"><XCircle size={14}/> Expired</div>;
          default: return null;
      }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fade-in">
        <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={onReset} className="!p-2 h-auto">
                <ArrowLeft size={16} />
            </Button>
            <div>
                <h1 className="font-serif text-3xl text-[#0A0B0F] dark:text-white">Invoice Ready</h1>
                <p className="text-gray-500 text-sm">Share this QR code or link to receive payment.</p>
            </div>
        </div>
        
        <Card className="p-8">
            <div className="flex flex-col md:flex-row items-center gap-8">
                <div className="p-4 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-white/10 shadow-lg">
                    <QRCodeCanvas
                      value={link}
                      size={180}
                      bgColor={"#ffffff"}
                      fgColor={"#0A0B0F"}
                      level={"H"}
                      includeMargin={false}
                    />
                </div>

                <div className="flex-1 space-y-4 text-sm text-gray-500 dark:text-gray-400 w-full">
                    <div className="flex justify-between items-center">
                        <span className="font-bold text-lg text-[#0A0B0F] dark:text-white">Amount</span>
                        <span className="font-mono font-bold text-lg text-[#0A0B0F] dark:text-white">{invoice.amount} {invoice.token}</span>
                    </div>
                     <div className="flex justify-between items-center">
                        <span>Chain</span>
                        <span className="font-bold">{invoice.chain.toUpperCase()}</span>
                    </div>
                    <div className="flex justify-between items-center">
                        <span>Status</span>
                        {getStatusChip()}
                    </div>
                    <div className="pt-4 border-t border-gray-100 dark:border-white/10">
                        <div className="font-bold text-xs uppercase tracking-wider mb-2 text-gray-400">Payment Address</div>
                        <p className="break-all font-mono text-xs text-gray-600 dark:text-gray-300">{invoice.address}</p>
                    </div>
                </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={() => copy(invoice.address)}
                  variant="outline"
                  className="w-full"
                  icon={<Copy size={16} />}
                >
                  Copy Address
                </Button>

                <a
                  href={link}
                  className="w-full inline-flex items-center justify-center rounded-btn font-bold transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed px-6 py-3 text-sm bg-ghost-jet text-white hover:bg-black/80 focus:ring-ghost-jet/50 shadow-md shadow-ghost-jet/20"
                >
                  <Zap className="mr-2 h-4 w-4" /> Open in Wallet
                </a>
            </div>
        </Card>
    </div>
  );
};